/**
 * Area Role - Validate Pillar
 *
 * Validação semântica de valores de área.
 * Zero dependências externas - pode ser usado standalone.
 *
 * @example
 * import { validateArea, isValidArea } from '@attrx/role-morphic/area/validate';
 *
 * validateArea(100);   // { valid: true, errors: [] }
 * validateArea(-5);    // { valid: false, errors: ['Area cannot be negative'] }
 * isValidArea(100);    // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface AreaValidationConfig {
  min?: number;
  max?: number;
  integer?: boolean;
  minError?: string;
  maxError?: string;
}

// =============================================================================
// CONFIG
// =============================================================================

/**
 * Configuração padrão de validação para área
 */
export const AREA_VALIDATION_CONFIG: AreaValidationConfig = {
  min: 0,
  minError: "Area cannot be negative",
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de área
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns Resultado da validação com erros se houver
 *
 * @example
 * validateArea(100);              // { valid: true, errors: [] }
 * validateArea(-5);               // { valid: false, errors: ['Area cannot be negative'] }
 * validateArea(150, { max: 100 }) // { valid: false, errors: ['Value must be at most 100'] }
 */
export function validateArea(
  value: unknown,
  config: AreaValidationConfig = AREA_VALIDATION_CONFIG
): ValidationResult {
  const errors: string[] = [];

  // Type check
  if (typeof value !== "number") {
    return { valid: false, errors: ["Value must be a number"] };
  }

  // Finite check
  if (!Number.isFinite(value)) {
    return { valid: false, errors: ["Value must be finite"] };
  }

  // Min check
  if (config.min !== undefined && value < config.min) {
    const msg = config.minError ?? `Value must be at least ${config.min}`;
    errors.push(msg);
  }

  // Max check
  if (config.max !== undefined && value > config.max) {
    const msg = config.maxError ?? `Value must be at most ${config.max}`;
    errors.push(msg);
  }

  // Integer check
  if (config.integer && !Number.isInteger(value)) {
    errors.push("Value must be an integer");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Verifica se um valor de área é válido
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidArea(100);  // true
 * isValidArea(-5);   // false
 */
export function isValidArea(
  value: unknown,
  config: AreaValidationConfig = AREA_VALIDATION_CONFIG
): boolean {
  return validateArea(value, config).valid;
}
